/**
 * Contract Date Calculation Validation Schemas
 * Zod schemas for validating contract date calculation requests and responses
 * Ensures data integrity for automatic date calculation feature
 */

import { z } from 'zod';

// ==================== Base Schemas ====================

/**
 * UUID validation schema
 */
const uuidSchema = z.string().guid('Invalid UUID format');

/**
 * ISO date string validation schema (supports both date and datetime formats)
 */
const isoDateSchema = z.string().refine((val) => {
  // Support both YYYY-MM-DD and full ISO datetime strings
  return !isNaN(Date.parse(val));
}, 'Invalid date format');

/**
 * FlexibleDate object schema matching backend shared.FlexibleDate type
 * Backend uses this format for date-only values without timezone concerns
 */
const flexibleDateSchema = z.object({
  time: z.string().datetime().describe('ISO datetime string contained in FlexibleDate object')
});

/**
 * Error code enum schema
 */
const errorCodeSchema = z.enum(
  ['CHILD_NOT_FOUND', 'INVALID_REQUEST', 'CALCULATION_ERROR', 'PERMISSION_DENIED'],
  {
    error: () => 'Invalid error code'
  }
);

// ==================== Request Schema ====================

/**
 * Backend request schema (sent to API)
 * Only includes fields that the backend API requires
 * IMPORTANT: Backend only accepts FlexibleDate format ({ time: string })
 */
export const contractDateCalculationRequestSchema = z.object({
  child_birthdate: flexibleDateSchema.describe('Birth date of the child (FlexibleDate object: { time: RFC3339 date-time })'),
  childcare_id: uuidSchema.describe('UUID of the childcare location (used to determine canton)'),
  organization_id: uuidSchema.optional().describe('Optional organization ID for cross-org operations'),
});

/**
 * Client-only schema for mutation variables
 * This schema is used in TanStack Query mutations
 * Uses string for childBirthdate since the hook converts it to FlexibleDate format
 */
export const contractDateCalculationVariablesSchema = z.object({
  childBirthdate: isoDateSchema.describe('Birth date of the child (ISO date string - will be converted to FlexibleDate format)'),
  childcareId: uuidSchema.describe('UUID of the childcare location (used to determine canton)'),
  organizationId: uuidSchema.optional().describe('Optional organization ID for cross-org operations'),
});

// ==================== Metadata Schema ====================

/**
 * Contract date calculation metadata schema
 * Validates the metadata about the calculation process - matches backend structure
 */
export const contractDateCalculationMetadataSchema = z
  .object({
    canton: z
      .string()
      .length(2)
      .optional()
      .describe('Canton used for calculation'),
    daycare_min_age_months: z
      .number()
      .int()
      .min(0)
      .optional()
      .describe('Minimum age for daycare in months'),
    kindergarten_min_age_months: z
      .number()
      .int()
      .min(0)
      .optional()
      .describe('Minimum age for kindergarten in months'),
    child_age_at_kindergarten: z
      .number()
      .int()
      .min(0)
      .optional()
      .describe('Child age when starting kindergarten in months'),
    kindergarten_start_date: isoDateSchema
      .optional()
      .describe('Date when child starts kindergarten'),
  })
  .transform((data) => ({
    canton: data.canton,
    daycareMinAgeMonths: data.daycare_min_age_months,
    kindergartenMinAgeMonths: data.kindergarten_min_age_months,
    childAgeAtKindergarten: data.child_age_at_kindergarten,
    kindergartenStartDate: data.kindergarten_start_date,
  }));

// ==================== Response Schema ====================

/**
 * Contract date calculation response schema
 * Validates the complete response from the calculation endpoint
 */
export const contractDateCalculationResponseSchema = z
  .object({
    // Calculated dates
    start_date: isoDateSchema
      .nullable()
      .optional()
      .describe('Calculated contract start date'),
    end_date: isoDateSchema
      .nullable()
      .optional()
      .describe('Calculated contract end date (optional for open-ended contracts)'),

    // Metadata about the calculation
    metadata: contractDateCalculationMetadataSchema.optional(),
  })
  .superRefine((data, ctx) => {
    // Ensure end date is after start date (only when both dates exist)
    if (!data.end_date || !data.start_date) {
      return; // Open-ended contracts or missing dates are valid
    }
    const start = new Date(data.start_date);
    const end = new Date(data.end_date);
    if (!(end > start)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date must be after start date',
        path: ['end_date'],
      });
    }
  })
  .transform((data) => ({
    startDate: data.start_date ?? undefined,
    endDate: data.end_date ?? undefined,
    metadata: data.metadata,
  }));

// ==================== Error Schema ====================

/**
 * Contract date calculation error schema
 * Validates error responses from the calculation endpoint
 */
export const contractDateCalculationErrorSchema = z.object({
  error: z.string().min(1, 'Error message is required'),
  code: errorCodeSchema,
  details: z.record(z.string(), z.unknown()).optional().describe('Additional error details'),
});

// ==================== Helper Schemas ====================

/**
 * Date range schema for utility functions
 */
export const dateRangeSchema = z.object({
  start: isoDateSchema,
  end: isoDateSchema,
}).refine(
  (data) => {
    const start = new Date(data.start);
    const end = new Date(data.end);
    return end >= start;
  },
  {
    message: 'End date must be after or equal to start date',
    path: ['end'],
  }
);

// ==================== Type Exports ====================

// Inferred types from schemas
export type ContractDateCalculationRequest = z.infer<typeof contractDateCalculationRequestSchema>;
export type ContractDateCalculationVariables = z.infer<typeof contractDateCalculationVariablesSchema>;
export type ContractDateCalculationMetadata = z.infer<typeof contractDateCalculationMetadataSchema>;
export type ContractDateCalculationResponse = z.infer<typeof contractDateCalculationResponseSchema>;
export type ContractDateCalculationError = z.infer<typeof contractDateCalculationErrorSchema>;
export type DateRange = z.infer<typeof dateRangeSchema>;

// ==================== Validation Helpers ====================

/**
 * Validate a contract date calculation request
 */
export const validateCalculationRequest = (data: unknown) => {
  return contractDateCalculationRequestSchema.safeParse(data);
};

/**
 * Validate a contract date calculation response
 */
export const validateCalculationResponse = (data: unknown) => {
  return contractDateCalculationResponseSchema.safeParse(data);
};

/**
 * Validate a calculation error response
 */
export const validateCalculationError = (data: unknown) => {
  return contractDateCalculationErrorSchema.safeParse(data);
};

/**
 * Type guard for calculation response
 */
export const isValidCalculationResponse = (data: unknown): data is ContractDateCalculationResponse => {
  return validateCalculationResponse(data).success;
};

/**
 * Type guard for calculation error
 */
export const isValidCalculationError = (data: unknown): data is ContractDateCalculationError => {
  return validateCalculationError(data).success;
};
